import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import OwTitle from "@components/shared/OwTitle/OwTitle";
import OwButton from "@components/shared/OwButton/OwButton";
import OwLabel from "@components/shared/OwLabel/OwLabel";
import { OwFnInput } from "@components/shared/OwFnInput/OwFnInput";
import Typeahead from "@components/shared/Typeahead/Typeahead";
import { useAppContextNonNull } from "@contexts/AppContext";
import { buildRestUrlWithQuery, buildAuthHeadersForFetch } from "@services/RestService";
import RestEndpointsEnum from "@models/RestEndpointsEnum";
import EndpointsEnum from "@models/EndpointsEnum";
import ICatalog from "@models/ICatalog";
import ICatalog_Item from "@models/ICatalog_Item";
import IOnGetCatalogsDTO from "@models/DTO/IOnGetCatalogsDTO";
import IOnTypeaheadCatalogItemDTO from "@models/DTO/IOnTypeaheadCatalogItemDTO";
import ITypeaheadCatalogItemDTO from "@models/DTO/ITypeaheadCatalogItemDTO";
import IProcessSerialDTO from "@models/DTO/IProcessSerialDTO";

type Props = {
    cancel: (refresh?: boolean) => void;
};

type FormData = {
    catalogGuid: string;
    catalogItemGuid: string;
    serial: string;
};

const validationSchema = yup.object().shape({
    catalogGuid: yup.string().required("Please select a catalog"),
    catalogItemGuid: yup.string().required("Please select a SKU"),
    serial: yup.string().trim().required("Please enter a serial number"),
});

export const CreateSerialSidebar = (props: Props) => {
    const { socket, userModel, supplierModel, vendorModel } = useAppContextNonNull();

    const [catalogs, setCatalogs] = useState<ICatalog[]>([]);
    const [typeaheadItems, setTypeaheadItems] = useState<ICatalog_Item[]>([]);
    const [selectedCatalogItem, setSelectedCatalogItem] = useState<ICatalog_Item | undefined>(undefined);
    const [clearTypeahead, setClearTypeahead] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string>("");

    const {
        setValue,
        handleSubmit,
        watch,
        formState: { errors },
    } = useForm<FormData>({
        resolver: yupResolver(validationSchema) as any,
        defaultValues: {
            catalogGuid: "",
            catalogItemGuid: "",
            serial: "",
        },
    });

    const catalogGuid = watch("catalogGuid");

    // Fetch catalogs on mount
    const fetchCatalogs = useCallback(async () => {
        try {
            const url = buildRestUrlWithQuery({
                endpoint: RestEndpointsEnum.CatalogsGetCatalogs,
            });

            const headers = buildAuthHeadersForFetch({
                userModel,
                supplierModel,
                vendorModel,
            });

            const response = await fetch(url, {
                method: "GET",
                headers,
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch catalogs: ${response.statusText}`);
            }

            const data = (await response.json()) as IOnGetCatalogsDTO;
            setCatalogs(data.catalogs || []);
        } catch (error) {
            console.error("Error fetching catalogs:", error);
        }
    }, [userModel, supplierModel, vendorModel]);

    useEffect(() => {
        fetchCatalogs();
    }, []);

    // Socket listener for typeahead results
    useEffect(() => {
        const onTypeaheadCatalogItem = (data: IOnTypeaheadCatalogItemDTO) => {
            if (data) {
                setTypeaheadItems(data.catalog_items || []);
            }
        };

        socket.on(EndpointsEnum.CatalogItemOnTypeaheadCatalogItem, onTypeaheadCatalogItem);

        return () => {
            socket.removeListener(EndpointsEnum.CatalogItemOnTypeaheadCatalogItem, onTypeaheadCatalogItem);
        };
    }, [socket]);

    // Typeahead search function
    const doTypeahead = (searchPhrase: string) => {
        if (!searchPhrase.length || !catalogGuid) return;

        const data: ITypeaheadCatalogItemDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            catalogGuid,
            name: searchPhrase,
            onlyActive: true,
            limit: 100,
            options: {
                searchName: true,
                searchSku: true,
                searchDescription: false,
                searchUpc: false,
            },
        };

        socket.emit(EndpointsEnum.CatalogItemTypeaheadCatalogItem, data);
    };

    // Handle catalog selection change
    const handleCatalogChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const guid = e.target.value;
        setValue("catalogGuid", guid);

        // Reset SKU selection
        setValue("catalogItemGuid", "");
        setSelectedCatalogItem(undefined);
        setTypeaheadItems([]);
        setClearTypeahead(true);
    };

    // Handle typeahead item selection
    const selectItem = (item: ICatalog_Item) => {
        setSelectedCatalogItem(item);
        setValue("catalogItemGuid", item.guid);
    };

    // Clear typeahead callback
    const clearTypeaheadFn = () => {
        setClearTypeahead(false);
    };

    // Handle serial input change
    const handleSerialChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setValue("serial", e.target.value);
    };

    // Submit handler
    const onSubmit = async (data: FormData) => {
        try {
            setIsSaving(true);
            setErrorMessage("");

            const url = buildRestUrlWithQuery({
                endpoint: RestEndpointsEnum.SerialsProcessSerial,
            });

            const headers = buildAuthHeadersForFetch({
                userModel,
                supplierModel,
                vendorModel,
            });

            const body: Partial<IProcessSerialDTO> = {
                catalogItemGuid: data.catalogItemGuid,
                serial: data.serial.trim(),
            };

            const response = await fetch(url, {
                method: "POST",
                headers,
                body: JSON.stringify(body),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => null);
                throw new Error(errorData?.message || `Failed to save serial: ${response.statusText}`);
            }

            props.cancel(true);
        } catch (error: any) {
            console.error("Error processing serial:", error);
            setErrorMessage(error.message || "Error saving serial");
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className={"CreateSerialSidebar p-2"}>
            <div className={"text-end"}>
                <OwButton centerIconClass={"fas fa-times"} fn={() => props.cancel(false)} className={"btn-danger btn-sm"} />
            </div>

            <OwTitle title={"create_serial"} />
            <hr />

            <form onSubmit={handleSubmit(onSubmit)}>
                {/* Catalog Selection */}
                <div className={"mb-3"}>
                    <OwLabel title={"catalog"} />
                    <select className={"form-select form-select-sm"} value={catalogGuid} onChange={handleCatalogChange}>
                        <option value={""}>-- Select Catalog --</option>
                        {catalogs.map((catalog) => (
                            <option key={catalog.guid} value={catalog.guid}>
                                {catalog.name}
                            </option>
                        ))}
                    </select>
                    {errors.catalogGuid && <div className={"text-danger small mt-1"}>{errors.catalogGuid.message}</div>}
                </div>

                {/* SKU Typeahead */}
                <div className={"mb-3"}>
                    <OwLabel title={"sku"} />
                    {catalogGuid ? (
                        <Typeahead
                            typeaheadItems={typeaheadItems}
                            clearTypeaheadCallback={clearTypeaheadFn}
                            clearTypeahead={clearTypeahead}
                            titleProp="sku"
                            subTitleProp="name"
                            isSelectableFn={() => true}
                            maxResults={100}
                            placeholder="Search by SKU or Name"
                            selectedItem={selectedCatalogItem}
                            selectedItemFn={selectItem}
                            typeaheadApiFn={doTypeahead}
                            scrollableList={true}
                            defaultClassName={"form-control form-control-sm"}
                        />
                    ) : (
                        <input className={"form-control form-control-sm"} disabled placeholder={"Select a catalog first"} />
                    )}
                    {errors.catalogItemGuid && <div className={"text-danger small mt-1"}>{errors.catalogItemGuid.message}</div>}
                </div>

                {/* Serial Number */}
                <div className={"mb-3"}>
                    <OwLabel title={"serial"} />
                    <OwFnInput fn={handleSerialChange} name={"serial"} value={watch("serial")} placeholder={"Enter serial number"} />
                    {errors.serial && <div className={"text-danger small mt-1"}>{errors.serial.message}</div>}
                </div>

                {/* Error Message */}
                {errorMessage && <div className={"alert alert-danger py-2 small"}>{errorMessage}</div>}

                {/* Action Buttons */}
                <div className={"d-flex justify-content-end mt-3"}>
                    <OwButton
                        title={"save"}
                        type={"submit"}
                        className={"btn-primary btn-sm me-2"}
                        disabled={isSaving}
                        active={isSaving}
                        disableOnActive={true}
                    />
                    <OwButton title={"cancel"} fn={() => props.cancel(false)} className={"btn-danger btn-sm"} disabled={isSaving} />
                </div>
            </form>
        </div>
    );
};
